import type {
  AdminArtifactContent,
  AdminArtifactSummary,
  AdminEmbeddingsStatus,
  AdmissionStats,
  AdminNotification,
  AdminOverview,
  AuthResponse,
  BulkJob,
  BulkOrganizeRequest,
  BulkOrganizeResponse,
  BulkSubmitRequest,
  BulkSubmitResponse,
  CreateLibraryRequest,
  DeadLetter,
  IngestAdmission,
  LibraryMetrics,
  LibraryDeletionResult,
  LibraryProfile,
  LibraryProfilePatch,
  LibrarySourcePatch,
  LibraryQuality,
  LibraryVersion,
  LibraryVersionDiscovery,
  LibraryVersionValidation,
  NotificationCounts,
  QuarantineLLMSummary,
  QuarantineReport,
  PublishedAggregate,
  RunEvent,
  RunState,
  SourceInspection,
  SourceRecord,
  StageName,
  SubmitSourceBatchResponse,
  SubmitSourceRequest,
  UsageAdminReport,
  UsageEventsReport,
  UsageMissesReport,
  UsageLimitPolicy,
  UserRequest,
  UserRequestPatch
} from "./types";

function defaultAPIBaseURL() {
  if (import.meta.env.VITE_OZ_API_URL) return import.meta.env.VITE_OZ_API_URL;
  if (window.location.hostname === "admin.tryoz.dev") return window.location.origin;
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return `${window.location.protocol}//${window.location.hostname}:8080`;
  }
  return "http://localhost:8080";
}

const defaultBaseURL = defaultAPIBaseURL();

export class AdminAPI {
  private baseURL: string;

  constructor(baseURL = import.meta.env.VITE_OZ_API_URL || defaultBaseURL) {
    this.baseURL = baseURL.replace(/\/$/, "");
  }

  setBaseURL(baseURL: string) {
    this.baseURL = baseURL.trim().replace(/\/$/, "") || defaultBaseURL;
  }

  eventURL(path: string) {
    return new URL(this.baseURL + path).toString();
  }

  me() {
    return this.request<AuthResponse>("/admin/auth/me");
  }

  login(email: string, password: string) {
    return this.request<AuthResponse>("/admin/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password })
    });
  }

  register(email: string, password: string, displayName: string) {
    return this.request<AuthResponse>("/admin/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, displayName })
    });
  }

  logout() {
    return this.request<{ status: string }>("/admin/auth/logout", {
      method: "POST",
      body: "{}"
    });
  }

  overview() {
    return this.request<AdminOverview>("/admin/overview");
  }

  usage() {
    return this.request<UsageAdminReport>("/admin/usage");
  }

  usageEvents(scopeType: string, scopeId: string, query = "", limit = 50) {
    const params = new URLSearchParams({ scope: scopeType, id: scopeId, limit: String(limit) });
    if (query.trim()) params.set("q", query.trim());
    return this.request<UsageEventsReport>(`/admin/usage/events?${params.toString()}`);
  }

  usageMisses(query = "", limit = 200) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (query.trim()) params.set("q", query.trim());
    return this.request<UsageMissesReport>(`/admin/usage/misses?${params.toString()}`);
  }

  updateUsagePolicy(body: UsageLimitPolicy) {
    return this.request<UsageLimitPolicy>("/admin/usage/policies", {
      method: "PATCH",
      body: JSON.stringify(body)
    });
  }

  libraries() {
    return this.request<LibraryProfile[]>("/admin/libraries?limit=5000");
  }

  userRequests(kind: UserRequest["kind"], status = "") {
    const params = new URLSearchParams({ kind, limit: "250" });
    if (status) params.set("status", status);
    return this.request<UserRequest[]>(`/admin/user-requests?${params.toString()}`);
  }

  updateUserRequest(requestID: string, body: UserRequestPatch) {
    return this.request<UserRequest>(`/admin/user-requests/${encodeURIComponent(requestID)}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    });
  }

  createLibrary(body: CreateLibraryRequest) {
    return this.request<SubmitSourceBatchResponse>("/admin/libraries", {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  inspectSource(body: CreateLibraryRequest) {
    return this.request<SourceInspection>("/admin/sources/inspect", {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  library(libraryID: string) {
    return this.request<LibraryProfile>(`/admin/libraries/${encodeURIComponent(libraryID)}`);
  }

  updateLibrary(libraryID: string, body: LibraryProfilePatch) {
    return this.request<LibraryProfile>(`/admin/libraries/${encodeURIComponent(libraryID)}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    });
  }

  deleteLibrary(libraryID: string, confirmLibraryId: string) {
    return this.request<LibraryDeletionResult>(`/admin/libraries/${encodeURIComponent(libraryID)}`, {
      method: "DELETE",
      body: JSON.stringify({ confirmLibraryId })
    });
  }

  librarySources(libraryID: string) {
    return this.request<SourceRecord[]>(`/admin/libraries/${encodeURIComponent(libraryID)}/sources`);
  }

  addLibrarySource(libraryID: string, body: CreateLibraryRequest) {
    return this.request<SubmitSourceBatchResponse>(`/admin/libraries/${encodeURIComponent(libraryID)}/sources`, {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  updateLibrarySource(libraryID: string, sourceID: string, body: LibrarySourcePatch) {
    return this.request<SourceRecord>(
      `/admin/libraries/${encodeURIComponent(libraryID)}/sources/${encodeURIComponent(sourceID)}`,
      { method: "PATCH", body: JSON.stringify(body) }
    );
  }

  refreshLibrarySource(libraryID: string, sourceID: string, force: boolean) {
    const suffix = force ? "?force=true" : "";
    return this.request<SubmitSourceBatchResponse>(
      `/admin/libraries/${encodeURIComponent(libraryID)}/sources/${encodeURIComponent(sourceID)}/refresh${suffix}`,
      { method: "POST", body: "{}" }
    );
  }

  libraryAggregate(libraryID: string, version = "") {
    const suffix = version ? `?version=${encodeURIComponent(version)}` : "";
    return this.request<PublishedAggregate>(`/admin/libraries/${encodeURIComponent(libraryID)}/aggregate${suffix}`);
  }

  rebuildLibraryAggregate(libraryID: string, version = "") {
    const suffix = version ? `?version=${encodeURIComponent(version)}` : "";
    return this.request<PublishedAggregate>(`/admin/libraries/${encodeURIComponent(libraryID)}/aggregate/rebuild${suffix}`, {
      method: "POST",
      body: "{}"
    });
  }

  libraryRuns(libraryID: string) {
    return this.request<RunState[]>(`/admin/libraries/${encodeURIComponent(libraryID)}/runs`);
  }

  refreshLibrary(libraryID: string, force: boolean) {
    const suffix = force ? "?force=true" : "";
    return this.request<SubmitSourceBatchResponse>(`/admin/libraries/${encodeURIComponent(libraryID)}/refresh${suffix}`, {
      method: "POST",
      body: "{}"
    });
  }

  addLibraryVersion(libraryID: string, body: Partial<LibraryVersion>) {
    return this.request<LibraryProfile>(`/admin/libraries/${encodeURIComponent(libraryID)}/versions`, {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  validateLibraryVersion(libraryID: string, body: Partial<LibraryVersion>) {
    return this.request<LibraryVersionValidation>(`/admin/libraries/${encodeURIComponent(libraryID)}/versions/validate`, {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  discoverLibraryVersions(libraryID: string, body: { limit?: number; maxRecommended?: number; includePrerelease?: boolean }) {
    return this.request<LibraryVersionDiscovery>(`/admin/libraries/${encodeURIComponent(libraryID)}/versions/discover`, {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  deleteLibraryVersion(libraryID: string, versionID: string) {
    return this.request<LibraryProfile>(
      `/admin/libraries/${encodeURIComponent(libraryID)}/versions/${encodeURIComponent(versionID)}`,
      { method: "DELETE" }
    );
  }

  refreshLibraryVersion(libraryID: string, versionID: string, force: boolean) {
    const suffix = force ? "?force=true" : "";
    return this.request<SubmitSourceBatchResponse>(
      `/admin/libraries/${encodeURIComponent(libraryID)}/versions/${encodeURIComponent(versionID)}/refresh${suffix}`,
      { method: "POST", body: "{}" }
    );
  }

  validateExistingLibraryVersion(libraryID: string, versionID: string) {
    return this.request<LibraryVersionValidation>(
      `/admin/libraries/${encodeURIComponent(libraryID)}/versions/${encodeURIComponent(versionID)}/validate`,
      { method: "POST", body: "{}" }
    );
  }

  libraryQuality(libraryID: string) {
    return this.request<LibraryQuality>(`/admin/libraries/${encodeURIComponent(libraryID)}/quality`);
  }

  libraryMetrics(libraryID: string) {
    return this.request<LibraryMetrics>(`/admin/libraries/${encodeURIComponent(libraryID)}/metrics`);
  }

  previewLibrary(libraryID: string, query: string, type = "json") {
    const params = new URLSearchParams({ q: query, type });
    return this.request<unknown>(`/admin/libraries/${encodeURIComponent(libraryID)}/preview/search?${params}`);
  }

  sources() {
    return this.request<SourceRecord[]>("/admin/sources");
  }

  submitSource(body: SubmitSourceRequest) {
    return this.request<SubmitSourceBatchResponse>("/admin/sources", {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  bulkOrganize(body: BulkOrganizeRequest) {
    return this.request<BulkOrganizeResponse>("/admin/bulk/organize", {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  bulkSubmit(body: BulkSubmitRequest) {
    return this.request<BulkSubmitResponse>("/admin/bulk/submit", {
      method: "POST",
      body: JSON.stringify(body)
    });
  }

  bulkJobs() {
    return this.request<BulkJob[]>("/admin/bulk/jobs");
  }

  admissions(filters: { status?: string; intent?: string; urgency?: string; limit?: number } = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.intent) params.set("intent", filters.intent);
    if (filters.urgency) params.set("urgency", filters.urgency);
    if (filters.limit) params.set("limit", String(filters.limit));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return this.request<IngestAdmission[]>(`/admin/admissions${suffix}`);
  }

  admissionStats() {
    return this.request<AdmissionStats>("/admin/admissions/stats");
  }

  dispatchAdmissions() {
    return this.request<AdmissionStats>("/admin/admissions/dispatch", {
      method: "POST",
      body: "{}"
    });
  }

  promoteAdmission(admissionID: string) {
    return this.request<IngestAdmission>(`/admin/admissions/${encodeURIComponent(admissionID)}/promote`, {
      method: "POST",
      body: "{}"
    });
  }

  cancelAdmission(admissionID: string, reason: string) {
    return this.request<IngestAdmission>(`/admin/admissions/${encodeURIComponent(admissionID)}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason })
    });
  }

  retryAdmission(admissionID: string) {
    return this.request<IngestAdmission>(`/admin/admissions/${encodeURIComponent(admissionID)}/retry`, {
      method: "POST",
      body: "{}"
    });
  }

  bulkJob(jobID: string) {
    return this.request<BulkJob>(`/admin/bulk/jobs/${encodeURIComponent(jobID)}`);
  }

  cancelBulkJob(jobID: string, reason: string) {
    return this.request<BulkJob>(`/admin/bulk/jobs/${encodeURIComponent(jobID)}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason })
    });
  }

  refreshSource(sourceID: string, force: boolean) {
    const suffix = force ? "?force=true" : "";
    return this.request<SubmitSourceBatchResponse>(`/admin/sources/${encodeURIComponent(sourceID)}/refresh${suffix}`, {
      method: "POST",
      body: "{}"
    });
  }

  runs(status = "", stage = "") {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (stage) params.set("stage", stage);
    const query = params.toString() ? `?${params}` : "";
    return this.request<RunState[]>(`/admin/runs${query}`);
  }

  run(runID: string) {
    return this.request<RunState>(`/admin/runs/${encodeURIComponent(runID)}`);
  }

  runEvents(runID: string) {
    return this.request<RunEvent[]>(`/admin/runs/${encodeURIComponent(runID)}/events`);
  }

  events(filters: Record<string, string>) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value.trim()) params.set(key, value.trim());
    }
    const suffix = params.toString() ? `?${params}` : "";
    return this.request<RunEvent[]>(`/admin/events${suffix}`);
  }

  notifications(filters: Record<string, string> = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value.trim()) params.set(key, value.trim());
    }
    const suffix = params.toString() ? `?${params}` : "";
    return this.request<AdminNotification[]>(`/admin/notifications${suffix}`);
  }

  notificationCounts() {
    return this.request<NotificationCounts>("/admin/notifications/counts");
  }

  markNotificationRead(notificationID: string) {
    return this.request<AdminNotification>(`/admin/notifications/${encodeURIComponent(notificationID)}/read`, {
      method: "POST",
      body: "{}"
    });
  }

  markAllNotificationsRead() {
    return this.request<NotificationCounts>("/admin/notifications/read-all", {
      method: "POST",
      body: "{}"
    });
  }

  runDiagnostics(runID: string) {
    return this.request<unknown>(`/admin/runs/${encodeURIComponent(runID)}/diagnostics`);
  }

  runArtifacts(runID: string) {
    return this.request<AdminArtifactSummary[]>(`/admin/runs/${encodeURIComponent(runID)}/artifacts`);
  }

  runArtifact(runID: string, path: string) {
    return this.request<AdminArtifactContent>(
      `/admin/runs/${encodeURIComponent(runID)}/artifact?path=${encodeURIComponent(path)}`
    );
  }

  runCrawlAudit(runID: string) {
    return this.request<unknown>(`/admin/runs/${encodeURIComponent(runID)}/crawl-audit`);
  }

  runQuarantine(runID: string) {
    return this.request<QuarantineReport>(`/admin/runs/${encodeURIComponent(runID)}/quarantine`);
  }

  analyzeRunQuarantine(runID: string, snippetIds: string[], rowIds: string[]) {
    return this.request<QuarantineLLMSummary>(`/admin/runs/${encodeURIComponent(runID)}/quarantine/analyze`, {
      method: "POST",
      body: JSON.stringify({ snippetIds, rowIds })
    });
  }

  decideRunQuarantine(runID: string, action: "approve" | "reject", snippetIds: string[], rowIds: string[], reason: string, retryFromEmbed: boolean) {
    return this.request<unknown>(`/admin/runs/${encodeURIComponent(runID)}/quarantine/decision`, {
      method: "POST",
      body: JSON.stringify({ action, snippetIds, rowIds, reason, retryFromEmbed })
    });
  }

  retryRun(runID: string) {
    return this.request<unknown>(`/admin/runs/${encodeURIComponent(runID)}/retry`, {
      method: "POST",
      body: "{}"
    });
  }

  retryStage(runID: string, stage: StageName) {
    return this.request<unknown>(`/admin/runs/${encodeURIComponent(runID)}/retry-stage/${stage}`, {
      method: "POST",
      body: "{}"
    });
  }

  cancelRun(runID: string, reason: string) {
    return this.request<RunState>(`/admin/runs/${encodeURIComponent(runID)}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason })
    });
  }

  deadLetters() {
    return this.request<DeadLetter[]>("/admin/dead-letters");
  }

  requeueDeadLetter(jobID: string) {
    return this.request<unknown>(`/admin/dead-letters/${encodeURIComponent(jobID)}/requeue`, {
      method: "POST",
      body: "{}"
    });
  }

  system() {
    return this.request<unknown>("/admin/system");
  }

  systemNATS() {
    return this.request<unknown>("/admin/system/nats");
  }

  systemQdrant() {
    return this.request<unknown>("/admin/system/qdrant");
  }

  systemRetrieval() {
    return this.request<unknown>("/admin/system/retrieval");
  }

  systemEmbeddings() {
    return this.request<AdminEmbeddingsStatus>("/admin/system/embeddings");
  }

  updateEmbeddingPools(clawEnabled: boolean, fireworksEnabled: boolean) {
    return this.request<AdminEmbeddingsStatus["indexPools"]>("/admin/system/embeddings", {
      method: "PATCH",
      body: JSON.stringify({ clawEnabled, fireworksEnabled })
    });
  }

  systemConfig() {
    return this.request<Record<string, unknown>>("/admin/system/config");
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(this.baseURL + path, {
      ...init,
      credentials: "include",
      headers: {
        "content-type": "application/json",
        ...(init.headers || {})
      }
    });
    if (!response.ok) {
      throw new Error((await response.text()) || response.statusText);
    }
    return response.json() as Promise<T>;
  }
}
